import React, { useState, useEffect } from 'react';
import './HijabStore.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8787';

export default function HijabStore() {
  const [view, setView] = useState('products'); // products, cart, checkout, admin-login, admin-panel
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [adminToken, setAdminToken] = useState(localStorage.getItem('adminToken') || '');
  const [orders, setOrders] = useState([]);

  // Filters
  const [colorFilter, setColorFilter] = useState('');
  const [styleFilter, setStyleFilter] = useState('');
  const [minPriceFilter, setMinPriceFilter] = useState('');
  const [maxPriceFilter, setMaxPriceFilter] = useState('');

  // Checkout form
  const [checkoutForm, setCheckoutForm] = useState({
    name: '',
    email: '',
    address: ''
  });

  // Admin form
  const [adminPassword, setAdminPassword] = useState('');
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price: '',
    color: '',
    style: '',
    stock: '',
    file: null
  });

  // Handle Stripe redirect params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success')) {
      setCart([]);
      alert('Payment successful! Your order has been placed.');
      window.history.replaceState({}, '', '/');
    } else if (params.get('cancelled')) {
      alert('Payment cancelled.');
      window.history.replaceState({}, '', '/');
    }
  }, []);

  // Load products
  useEffect(() => {
    if (view === 'products' || view === 'admin-panel') {
      fetchProducts();
    }
  }, [view, colorFilter, styleFilter, minPriceFilter, maxPriceFilter]);

  // Load orders when viewing admin panel
  useEffect(() => {
    if (view === 'admin-panel' && adminToken) {
      fetchOrders();
    }
  }, [view, adminToken]);

  const fetchProducts = async () => {
    try {
      const params = new URLSearchParams();
      if (colorFilter) params.append('color', colorFilter);
      if (styleFilter) params.append('style', styleFilter);
      if (minPriceFilter) params.append('min_price', minPriceFilter);
      if (maxPriceFilter) params.append('max_price', maxPriceFilter);

      const response = await fetch(`${API_URL}/api/products?${params}`);
      const data = await response.json();
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/orders?token=${adminToken}`);
      const data = await response.json();
      if (response.ok) {
        setOrders(data);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const addToCart = (product) => {
    const existing = cart.find(item => item.id === product.id);
    if (existing) {
      setCart(cart.map(item =>
        item.id === product.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
    }
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.id !== productId));
  };

  const calculateTotal = () => {
    return cart.reduce((total, item) => total + (parseFloat(item.price) * item.quantity), 0).toFixed(2);
  };

  const handleCheckout = async (e) => {
    e.preventDefault();

    const items = cart.map(item => ({
      product_id: item.id,
      quantity: item.quantity
    }));

    try {
      const response = await fetch(`${API_URL}/api/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_name: checkoutForm.name,
          customer_email: checkoutForm.email,
          shipping_address: checkoutForm.address,
          items
        })
      });

      const data = await response.json();

      if (response.ok && data.url) {
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        alert(`Checkout failed: ${data.error}`);
      }
    } catch (error) {
      alert('Checkout error: ' + error.message);
    }
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: adminPassword })
      });

      const data = await response.json();

      if (response.ok && data.token) {
        setAdminToken(data.token);
        localStorage.setItem('adminToken', data.token);
        setView('admin-panel');
        setAdminPassword('');
      } else {
        alert('Invalid password');
      }
    } catch (error) {
      alert('Login error: ' + error.message);
    }
  };

  const handleAdminLogout = () => {
    setAdminToken('');
    localStorage.removeItem('adminToken');
    setView('products');
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append('name', productForm.name);
    formData.append('description', productForm.description);
    formData.append('price', productForm.price);
    formData.append('color', productForm.color);
    formData.append('style', productForm.style);
    formData.append('stock', productForm.stock);
    if (productForm.file) {
      formData.append('file', productForm.file);
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/products?token=${adminToken}`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        alert('Product added successfully!');
        setProductForm({
          name: '',
          description: '',
          price: '',
          color: '',
          style: '',
          stock: '',
          file: null
        });
        fetchProducts();
      } else {
        alert(`Failed: ${data.error}`);
      }
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/products/${productId}?token=${adminToken}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        alert('Product deleted successfully!');
        fetchProducts();
      } else {
        const data = await response.json();
        alert(`Failed: ${data.error}`);
      }
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/orders/${orderId}?token=${adminToken}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        fetchOrders();
      } else {
        const data = await response.json();
        alert(`Failed: ${data.error}`);
      }
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  // =====================
  // RENDER VIEWS
  // =====================

  const renderHeader = () => (
    <header className="header">
      <div className="header-content">
        <div className="header-left">
          {view !== 'products' && (
            <button className="back-header-btn" onClick={() => setView('products')}>← Back</button>
          )}
          <h1 className="store-name" onClick={() => setView('products')}>Hijab Store</h1>
        </div>
        <div className="header-actions">
          {view !== 'admin-panel' && view !== 'admin-login' && (
            <>
              <button className="header-btn" onClick={() => setView('cart')}>
                Cart ({cart.length})
              </button>
              <button className="header-btn" onClick={() => setView('admin-login')}>
                Admin
              </button>
            </>
          )}
          {view === 'admin-panel' && (
            <button className="header-btn" onClick={handleAdminLogout}>
              Logout
            </button>
          )}
        </div>
      </div>
    </header>
  );

  const renderProducts = () => (
    <div className="products-view">
      <div className="filters">
        <input
          type="text"
          placeholder="Color"
          value={colorFilter}
          onChange={(e) => setColorFilter(e.target.value)}
          className="filter-input"
        />
        <input
          type="text"
          placeholder="Style"
          value={styleFilter}
          onChange={(e) => setStyleFilter(e.target.value)}
          className="filter-input"
        />
        <input
          type="number"
          placeholder="Min Price"
          value={minPriceFilter}
          onChange={(e) => setMinPriceFilter(e.target.value)}
          className="filter-input"
        />
        <input
          type="number"
          placeholder="Max Price"
          value={maxPriceFilter}
          onChange={(e) => setMaxPriceFilter(e.target.value)}
          className="filter-input"
        />
      </div>

      <div className="products-grid">
        {products.map(product => (
          <div key={product.id} className="product-card">
            {product.image_url && (
              <img src={product.image_url} alt={product.name} className="product-image" />
            )}
            <h3 className="product-name">{product.name}</h3>
            <p className="product-details">
              {product.color} • {product.style} • Stock: {product.stock}
            </p>
            <p className="product-price">${parseFloat(product.price).toFixed(2)}</p>
            {product.stock > 0 ? (
              <button className="add-to-cart-btn" onClick={() => addToCart(product)}>
                Add to cart
              </button>
            ) : (
              <button className="out-of-stock-btn" disabled>
                Out of stock
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderCart = () => (
    <div className="cart-view">
      <button className="back-btn" onClick={() => setView('products')}>← Back</button>
      <h2>Your Cart</h2>

      {cart.length === 0 ? (
        <p>Your cart is empty</p>
      ) : (
        <>
          <div className="cart-items">
            {cart.map(item => (
              <div key={item.id} className="cart-item">
                <div>
                  <h3>{item.name}</h3>
                  <p>${parseFloat(item.price).toFixed(2)} × {item.quantity} = ${(parseFloat(item.price) * item.quantity).toFixed(2)}</p>
                </div>
                <button className="remove-btn" onClick={() => removeFromCart(item.id)}>Remove</button>
              </div>
            ))}
          </div>

          <div className="cart-total">
            <h3>Total: ${calculateTotal()}</h3>
          </div>

          <button className="checkout-btn" onClick={() => setView('checkout')}>
            Proceed to Checkout
          </button>
        </>
      )}
    </div>
  );

  const renderCheckout = () => (
    <div className="checkout-view">
      <button className="back-btn" onClick={() => setView('cart')}>← Back</button>
      <h2>Checkout</h2>

      <form onSubmit={handleCheckout} className="checkout-form">
        <input
          type="text"
          placeholder="Full Name"
          value={checkoutForm.name}
          onChange={(e) => setCheckoutForm({...checkoutForm, name: e.target.value})}
          required
          className="form-input"
        />
        <input
          type="email"
          placeholder="Email"
          value={checkoutForm.email}
          onChange={(e) => setCheckoutForm({...checkoutForm, email: e.target.value})}
          required
          className="form-input"
        />
        <textarea
          placeholder="Shipping Address"
          value={checkoutForm.address}
          onChange={(e) => setCheckoutForm({...checkoutForm, address: e.target.value})}
          required
          className="form-textarea"
          rows="3"
        />

        <div className="checkout-total">
          <h3>Total: ${calculateTotal()}</h3>
        </div>

        <button type="submit" className="submit-btn">
          Proceed to Payment
        </button>
      </form>
    </div>
  );

  const renderAdminLogin = () => (
    <div className="admin-login-view">
      <div className="admin-login-card">
        <h2>Admin Login</h2>
        <form onSubmit={handleAdminLogin} className="admin-login-form">
          <input
            type="password"
            placeholder="Password"
            value={adminPassword}
            onChange={(e) => setAdminPassword(e.target.value)}
            required
            className="form-input"
          />
          <button type="submit" className="submit-btn">
            Login
          </button>
        </form>
        <button className="back-btn" onClick={() => setView('products')}>← Back to Store</button>
      </div>
    </div>
  );

  const renderAdminPanel = () => (
    <div className="admin-panel-view">
      <h2>Admin Panel</h2>

      <div className="admin-grid">
        <div className="admin-section">
          <h3>Add Product</h3>
          <form onSubmit={handleAddProduct} className="admin-form">
            <input
              type="text"
              placeholder="Name"
              value={productForm.name}
              onChange={(e) => setProductForm({...productForm, name: e.target.value})}
              required
              className="form-input"
            />
            <textarea
              placeholder="Description"
              value={productForm.description}
              onChange={(e) => setProductForm({...productForm, description: e.target.value})}
              className="form-textarea"
              rows="3"
            />
            <input
              type="number"
              step="0.01"
              placeholder="Price"
              value={productForm.price}
              onChange={(e) => setProductForm({...productForm, price: e.target.value})}
              required
              className="form-input"
            />
            <input
              type="text"
              placeholder="Color"
              value={productForm.color}
              onChange={(e) => setProductForm({...productForm, color: e.target.value})}
              className="form-input"
            />
            <input
              type="text"
              placeholder="Style"
              value={productForm.style}
              onChange={(e) => setProductForm({...productForm, style: e.target.value})}
              className="form-input"
            />
            <input
              type="number"
              placeholder="Stock"
              value={productForm.stock}
              onChange={(e) => setProductForm({...productForm, stock: e.target.value})}
              required
              className="form-input"
            />
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setProductForm({...productForm, file: e.target.files[0]})}
              className="form-file"
            />
            <button type="submit" className="submit-btn">
              Add Product
            </button>
          </form>
        </div>

        <div className="admin-section">
          <h3>Orders</h3>
          <div className="orders-table">
            {orders.map(order => (
              <div key={order.id} className="order-row">
                <div>
                  <p><strong>#{order.id}</strong> - {order.customer_name}</p>
                  <p>${parseFloat(order.total_amount).toFixed(2)}</p>
                </div>
                <select
                  value={order.status}
                  onChange={(e) => handleUpdateOrderStatus(order.id, e.target.value)}
                  className="status-select"
                >
                  <option value="pending">Pending</option>
                  <option value="completed">Completed</option>
                  <option value="shipped">Shipped</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="admin-products">
        <h3>Manage Products</h3>
        <div className="products-grid">
          {products.map(product => (
            <div key={product.id} className="product-card">
              {product.image_url && (
                <img src={product.image_url} alt={product.name} className="product-image" />
              )}
              <h3 className="product-name">{product.name}</h3>
              <p className="product-details">
                {product.color} • {product.style} • Stock: {product.stock}
              </p>
              <p className="product-price">${parseFloat(product.price).toFixed(2)}</p>
              <button className="delete-btn" onClick={() => handleDeleteProduct(product.id)}>
                Delete
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="app">
      {renderHeader()}
      <main className="main">
        {view === 'products' && renderProducts()}
        {view === 'cart' && renderCart()}
        {view === 'checkout' && renderCheckout()}
        {view === 'admin-login' && renderAdminLogin()}
        {view === 'admin-panel' && renderAdminPanel()}
      </main>
    </div>
  );
}